import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowDownUp,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  Search,
  Users,
} from 'lucide-react'
import { PeriodSelector } from '@/components/analytics'
import { ActionButton, Badge, Button, Card, ProgressBar } from '@/components/ui'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'
import { formatNumber, type MetricPeriod } from '@/lib/utils'
import { useAuthStore } from '@/store'
import { PedagogyAccessErrorCard } from '@/components/pedagogy/PedagogyAccessStates'
import { usePedagogyAccess } from '@/hooks/usePedagogyAccess'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import {
  listMcUsersForPedagogyAdmin,
  listPedagogySpecialistPartnerCoverage,
  setUserPedagogyLeadRole,
  setUserPedagogySpecialistRole,
} from '@/services/pedagogySpecialistService'
import {
  getPedagogyDepartmentQualitySummary,
  listPedagogyDashboard,
  type PedagogyDashboardRow,
  type PedagogyDepartmentQualitySummary,
} from '@/services/pedagogyDashboardService'
import type { McUserForPedagogyAdmin, PedagogySpecialistPartnerCoverageRow } from '@/types'

function displayName(item: {
  specialistName?: string | null
  tgUsername?: string | null
  specialist_name?: string | null
  tg_username?: string | null
  user_id?: string
  specialistUserId?: string
  specialist_user_id?: string
}) {
  return item.specialistName
    || item.specialist_name
    || item.tgUsername
    || item.tg_username
    || item.user_id
    || item.specialistUserId
    || item.specialist_user_id
    || 'Специалист'
}

function canManagePedagogyRole(user: McUserForPedagogyAdmin) {
  return user.access_level === 'standard'
    && (
      user.job_title == null
      || user.job_title === 'other'
      || user.job_title === 'pedagogy_specialist'
      || user.job_title === 'pedagogy_lead'
    )
}

function formatIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseIsoDateLocal(value: string): Date {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function calculateExpectedProgress(start: string, end: string): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const rangeStart = parseIsoDateLocal(start)
  const rangeEnd = parseIsoDateLocal(end)

  if (today < rangeStart) return 0
  if (today > rangeEnd) return 100

  const dayMs = 24 * 60 * 60 * 1000
  const totalDays = Math.max(1, Math.floor((rangeEnd.getTime() - rangeStart.getTime()) / dayMs) + 1)
  const elapsedDays = Math.max(0, Math.floor((today.getTime() - rangeStart.getTime()) / dayMs) + 1)

  return Math.min(100, Math.round((elapsedDays / totalDays) * 1000) / 10)
}

type SpecialistSortKey = 'specialist' | 'partners' | 'fact_plan' | 'completion' | 'latest_review'

export function PedagogySpecialistsPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const {
    loading: accessLoading,
    canManagePedagogy,
    canViewAllPedagogy,
    canViewPedagogySection,
    accessError,
    retryAccessCheck,
  } = usePedagogyAccess()

  const [periods, setPeriods] = useState<MetricPeriod[]>([])
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null)
  const [dashboardRows, setDashboardRows] = useState<PedagogyDashboardRow[]>([])
  const [coverageRows, setCoverageRows] = useState<PedagogySpecialistPartnerCoverageRow[]>([])
  const [qualitySummary, setQualitySummary] = useState<PedagogyDepartmentQualitySummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SpecialistSortKey>('completion')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [rolesSheetOpen, setRolesSheetOpen] = useState(false)
  const [mcUsers, setMcUsers] = useState<McUserForPedagogyAdmin[]>([])
  const [mcUsersLoading, setMcUsersLoading] = useState(false)
  const [mcUsersError, setMcUsersError] = useState<string | null>(null)
  const [busyUserId, setBusyUserId] = useState<string | null>(null)

  const selectedPeriod = useMemo(
    () => periods.find(period => period.period_id === selectedPeriodId) ?? null,
    [periods, selectedPeriodId]
  )

  const {
    selectedTimeFilter,
    setSelectedTimeFilter,
    timeFilterOptions,
    selectedRange,
  } = useAnalyticsTimeFilter(selectedPeriod, { isCurrentPeriod: true })

  useEffect(() => {
    let cancelled = false

    async function loadPeriods() {
      const supabase = getSupabase()
      const { data, error: periodsError } = await withAuthRecovery(() =>
        supabase.from('metric_periods').select('*').order('period_start', { ascending: false })
      )
      if (periodsError) throw periodsError
      if (cancelled) return

      const gateDate = new Date()
      gateDate.setDate(gateDate.getDate() + 3)
      const openUntil = formatIsoDate(gateDate)
      const today = formatIsoDate(new Date())
      const nextPeriods = ((data ?? []) as MetricPeriod[])
        .filter(period => period.period_start <= openUntil)

      setPeriods(nextPeriods)

      const current = nextPeriods.find(period => period.period_start <= today && today <= period.period_end) ?? nextPeriods[0] ?? null
      setSelectedPeriodId(current?.period_id ?? null)
    }

    void loadPeriods().catch(loadError => {
      if (cancelled) return
      console.error('Failed to load pedagogy periods:', loadError)
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить периоды')
    })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (selectedPeriodId == null) return
    let cancelled = false

    async function loadDashboard() {
      setLoading(true)
      setError(null)

      try {
        const coreDataPromise = Promise.all([
          listPedagogyDashboard(selectedPeriodId, selectedRange?.weekNumber ?? null),
          listPedagogySpecialistPartnerCoverage(),
          getPedagogyDepartmentQualitySummary(selectedPeriodId, selectedRange?.weekNumber ?? null),
        ])
        const [dashboard, coverage, quality] = await coreDataPromise

        if (cancelled) return
        setDashboardRows(dashboard)
        setCoverageRows(coverage)
        setQualitySummary(quality)
      } catch (loadError) {
        if (cancelled) return
        console.error('Failed to load pedagogy dashboard:', loadError)
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить раздел педагогов')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void loadDashboard()

    return () => {
      cancelled = true
    }
  }, [canManagePedagogy, selectedPeriodId, selectedRange?.weekNumber])

  const reloadMcUsers = () => {
    setMcUsersLoading(true)
    setMcUsersError(null)
    listMcUsersForPedagogyAdmin()
      .then(setMcUsers)
      .catch(err => setMcUsersError(err instanceof Error ? err.message : String(err)))
      .finally(() => setMcUsersLoading(false))
  }

  const openRolesSheet = () => {
    if (!canManagePedagogy) return
    setRolesSheetOpen(true)
    if (mcUsers.length === 0 && !mcUsersLoading) {
      reloadMcUsers()
    }
  }

  const handleToggleSpecialist = async (targetUser: McUserForPedagogyAdmin, isSpecialist: boolean) => {
    if (!isSpecialist && targetUser.partners_count > 0) {
      setMcUsersError(
        `Нельзя снять роль у ${displayName(targetUser)}: сначала передайте или снимите ${targetUser.partners_count} активных назначений на экране назначений франчайзи.`
      )
      return
    }

    setBusyUserId(targetUser.user_id)
    setMcUsersError(null)

    try {
      await setUserPedagogySpecialistRole({ userId: targetUser.user_id, isSpecialist })
      const freshDashboard = await listPedagogyDashboard(selectedPeriodId, selectedRange?.weekNumber ?? null)
      setDashboardRows(freshDashboard)
      reloadMcUsers()
    } catch (actionError) {
      setMcUsersError(actionError instanceof Error ? actionError.message : String(actionError))
    } finally {
      setBusyUserId(null)
    }
  }

  const handleToggleLead = async (targetUser: McUserForPedagogyAdmin, isLead: boolean) => {
    setBusyUserId(targetUser.user_id)
    setMcUsersError(null)

    try {
      await setUserPedagogyLeadRole({ userId: targetUser.user_id, isLead })
      const freshDashboard = await listPedagogyDashboard(selectedPeriodId, selectedRange?.weekNumber ?? null)
      setDashboardRows(freshDashboard)
      reloadMcUsers()
    } catch (actionError) {
      setMcUsersError(actionError instanceof Error ? actionError.message : String(actionError))
    } finally {
      setBusyUserId(null)
    }
  }

  const eligibleMcUsers = useMemo(
    () => mcUsers.filter(canManagePedagogyRole),
    [mcUsers]
  )

  const selectedPeriodIndex = useMemo(
    () => periods.findIndex(period => period.period_id === selectedPeriodId),
    [periods, selectedPeriodId]
  )

  const canGoToNewerPeriod = selectedPeriodIndex > 0
  const canGoToOlderPeriod = selectedPeriodIndex >= 0 && selectedPeriodIndex < periods.length - 1

  const stats = useMemo(() => {
    const activeCoverageRows = coverageRows.filter(row => row.has_active_studios !== false)
    const unassignedPartnersCount = activeCoverageRows.filter(row => !row.specialist_user_id).length
    const totalPartnersCount = activeCoverageRows.length
    const staleAssignmentsCount = coverageRows.filter(row => row.assignment_needs_attention).length
    const plan = dashboardRows.reduce((sum, row) => sum + row.plannedReviewsCount, 0)
    const fact = dashboardRows.reduce((sum, row) => sum + row.factReviewsCount, 0)
    const completionPct = plan > 0 ? Math.round((fact / plan) * 1000) / 10 : null
    const averageQualityPct = qualitySummary?.averageQualityPercent ?? null

    return {
      totalSpecialists: dashboardRows.length,
      totalPartnersCount,
      fact,
      plan,
      remaining: Math.max(plan - fact, 0),
      unassignedPartnersCount,
      staleAssignmentsCount,
      completionPct,
      averageQualityPct,
    }
  }, [coverageRows, dashboardRows, qualitySummary])

  const expectedProgressPct = useMemo(
    () => selectedRange ? calculateExpectedProgress(selectedRange.start, selectedRange.end) : null,
    [selectedRange]
  )

  const progressDeltaPct = stats.completionPct == null || expectedProgressPct == null
    ? null
    : Math.round((stats.completionPct - expectedProgressPct) * 10) / 10

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase()

    const nextRows = dashboardRows.filter(row => {
      if (!query) return true

      return [
        row.specialistName ?? '',
        row.tgUsername ?? '',
      ].some(value => value.toLowerCase().includes(query))
    })

    const completionSortValue = (row: PedagogyDashboardRow) => row.completionPct ?? -1
    const latestReviewSortValue = (row: PedagogyDashboardRow) => (
      row.latestReviewDate
        ? new Date(`${row.latestReviewDate}T00:00:00`).getTime()
        : -1
    )

    nextRows.sort((left, right) => {
      let result = 0

      switch (sortKey) {
        case 'specialist':
          result = displayName(left).localeCompare(displayName(right), 'ru')
          break
        case 'partners':
          result = left.partnersCount - right.partnersCount
          break
        case 'fact_plan':
          result = (left.factReviewsCount - right.factReviewsCount)
            || (left.plannedReviewsCount - right.plannedReviewsCount)
          break
        case 'latest_review':
          result = latestReviewSortValue(left) - latestReviewSortValue(right)
          break
        case 'completion':
        default: {
          const leftHasPlan = left.plannedReviewsCount > 0
          const rightHasPlan = right.plannedReviewsCount > 0

          if (leftHasPlan && rightHasPlan) {
            result = completionSortValue(left) - completionSortValue(right)
              || (left.factReviewsCount - right.factReviewsCount)
          } else if (!leftHasPlan && !rightHasPlan) {
            result = left.factReviewsCount - right.factReviewsCount
          } else {
            result = leftHasPlan ? 1 : -1
          }
          break
        }
      }

      if (result === 0) {
        result = displayName(left).localeCompare(displayName(right), 'ru')
      }

      return sortDirection === 'asc' ? result : -result
    })

    return nextRows
  }, [dashboardRows, search, sortDirection, sortKey])

  const toggleSort = (nextKey: SpecialistSortKey) => {
    if (sortKey === nextKey) {
      setSortDirection(current => current === 'asc' ? 'desc' : 'asc')
      return
    }

    setSortKey(nextKey)
    setSortDirection('desc')
  }

  if (accessError) return <PedagogyAccessErrorCard onRetry={retryAccessCheck} />
  if (!accessLoading && !canViewPedagogySection) {
    return (
      <Card>
        <div className="text-sm text-muted-foreground">
          Раздел педагогов доступен только специалистам по педагогам, руководителю педагогов и `advanced`.
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="rounded-xl border border-border bg-card px-2 shadow-sm">
        <PeriodSelector
          currentPeriod={selectedPeriod}
          onPrevPeriod={() => {
            if (!canGoToOlderPeriod) return
            setSelectedPeriodId(periods[selectedPeriodIndex + 1]?.period_id ?? null)
          }}
          onNextPeriod={() => {
            if (!canGoToNewerPeriod) return
            setSelectedPeriodId(periods[selectedPeriodIndex - 1]?.period_id ?? null)
          }}
          canGoPrev={canGoToOlderPeriod}
          canGoNext={canGoToNewerPeriod}
          showViewModeSelector={true}
          isCurrentPeriod={true}
          periodFilterValue={selectedTimeFilter}
          periodFilterOptions={timeFilterOptions}
          onPeriodFilterChange={setSelectedTimeFilter}
          selectedRange={selectedRange}
          compact
        />
      </div>

      {error && (
        <Card className="border border-error/30 bg-error/5 shadow-sm">
          <div className="text-sm text-error">{error}</div>
        </Card>
      )}

      <Card id="pedagogy-specialists-table" className="!p-4 border border-border shadow-sm">
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h1 className="text-base font-semibold">Специалисты</h1>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
              {formatNumber(filteredRows.length)}
            </span>
            <div className="hidden h-4 w-px bg-border sm:block" />
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span title="Выполнение плана отсмотров за выбранный период">
                Выполнено{' '}
                <strong className="tabular-nums text-foreground">{formatNumber(stats.fact)} из {formatNumber(stats.plan)}</strong>{' '}
                <strong className="tabular-nums text-primary">{stats.completionPct == null ? '—' : `${stats.completionPct.toFixed(1)}%`}</strong>
              </span>
              <span>
                Качество{' '}
                <strong className="tabular-nums text-foreground">{stats.averageQualityPct == null ? '—' : `${stats.averageQualityPct.toFixed(1)}%`}</strong>
              </span>
              {progressDeltaPct != null && (
                <span
                  title={`Ожидаемое выполнение на текущую дату: ${expectedProgressPct?.toFixed(1)}%`}
                  className={progressDeltaPct > 0 ? 'text-success' : progressDeltaPct < 0 ? 'text-error' : undefined}
                >
                  {progressDeltaPct > 0
                    ? `Темп выше ожидаемого на ${Math.abs(progressDeltaPct).toFixed(1)} п.п.`
                    : progressDeltaPct < 0
                      ? `Темп ниже ожидаемого на ${Math.abs(progressDeltaPct).toFixed(1)} п.п.`
                      : 'Темп соответствует ожидаемому'}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5" aria-label="Управление педагогическим направлением">
            {canManagePedagogy && (
              <CompactControlButton
                icon={<Users className="h-4 w-4" />}
                label="Назначения франчайзи"
                hasAttention={stats.unassignedPartnersCount > 0 || stats.staleAssignmentsCount > 0}
                onClick={() => navigate(pedagogyPaths.coverage)}
              />
            )}
            {canManagePedagogy && (
              <CompactControlButton
                icon={<CalendarDays className="h-4 w-4" />}
                label="Планирование нагрузки"
                onClick={() => navigate(pedagogyPaths.dispatch)}
              />
            )}
            {canManagePedagogy && (
              <CompactControlButton
                icon={<Users className="h-4 w-4" />}
                label={`Назначение специалистов · ${formatNumber(stats.totalSpecialists)}`}
                onClick={openRolesSheet}
              />
            )}
            <Button
              size="sm"
              onClick={() => navigate(pedagogyPaths.reviewNew)}
              className="ml-1 inline-flex shrink-0 items-center justify-center whitespace-nowrap"
            >
              <ClipboardList size={16} className="mr-1" />
              Новый отсмотр
            </Button>
          </div>
        </div>

        <div className="mt-3 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-[420px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="ФИО или @username"
              className="h-10 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

        </div>

        {loading ? (
          <div className="mt-4 text-sm text-muted-foreground">Загрузка раздела педагогов...</div>
        ) : filteredRows.length === 0 ? (
          <div className="mt-4 rounded-lg bg-muted/40 px-3 py-6 text-center text-sm text-muted-foreground">
            Под текущий фильтр специалисты не найдены.
          </div>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-[13px] leading-snug">
              <thead className="text-left text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="px-3 py-2.5 font-medium">
                    <SortButton label="Специалист" active={sortKey === 'specialist'} direction={sortDirection} onClick={() => toggleSort('specialist')} />
                  </th>
                  <th className="px-3 py-2.5 font-medium">
                    <SortButton label="Контур" active={sortKey === 'partners'} direction={sortDirection} onClick={() => toggleSort('partners')} />
                  </th>
                  <th className="min-w-[220px] px-3 py-2.5 font-medium">
                    <SortButton label="Выполнение плана" active={sortKey === 'completion' || sortKey === 'fact_plan'} direction={sortDirection} onClick={() => toggleSort('completion')} />
                  </th>
                  <th className="px-3 py-2.5 font-medium">
                    <SortButton label="Последний отсмотр" active={sortKey === 'latest_review'} direction={sortDirection} onClick={() => toggleSort('latest_review')} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => {
                  const canOpenSpecialist = canViewAllPedagogy || row.specialistUserId === user?.id
                  return (
                    <tr key={row.specialistUserId} className="border-b border-border/70 align-top transition-colors hover:bg-muted/25">
                      <td className="px-3 py-2.5">
                        {canOpenSpecialist ? (
                          <button
                            type="button"
                            onClick={() => navigate(`${pedagogyPaths.specialist(row.specialistUserId)}?tab=overview&periodId=${row.periodId}`)}
                            className="group inline-flex items-center gap-1.5 text-left font-semibold hover:text-primary"
                          >
                            <span>{displayName(row)}</span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                          </button>
                        ) : (
                          <div className="font-semibold">{displayName(row)}</div>
                        )}
                        <div className="mt-1 flex flex-wrap gap-2">
                          {row.pedagogyRoleEndDate && (
                            <Badge text={`до ${row.pedagogyRoleEndDate}`} variant="warning" className="px-1.5 py-0 text-[10px]" />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div>{formatNumber(row.partnersCount)} франчайзи</div>
                        <div className="text-xs text-muted-foreground">{formatNumber(row.studiosCount)} студий</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="max-w-[240px]">
                          <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
                            <span className="font-semibold tabular-nums text-foreground">
                              {formatNumber(row.factReviewsCount)} / {formatNumber(row.plannedReviewsCount)}
                            </span>
                            <span className="font-medium tabular-nums text-muted-foreground">
                              {row.completionPct == null ? '—' : `${row.completionPct.toFixed(1)}%`}
                            </span>
                          </div>
                          <ProgressBar value={row.completionPct ?? 0} fillClassName="bg-primary" />
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">
                        {row.latestReviewDate ?? '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {canManagePedagogy && (
        <BottomSheet
          isOpen={rolesSheetOpen}
          onClose={() => setRolesSheetOpen(false)}
          title="Роли специалистов по педагогам"
        >
          <div className="p-4">
            {mcUsersError && (
              <div className="flex items-center gap-1 rounded-lg border border-error/20 bg-error/5 px-3 py-2 text-xs text-error">
                <AlertTriangle size={12} /> {mcUsersError}
              </div>
            )}

            {mcUsersLoading ? (
              <div className="mt-4 text-sm text-muted-foreground">Загрузка пользователей...</div>
            ) : eligibleMcUsers.length === 0 ? (
              <div className="mt-4 text-sm text-muted-foreground">
                Нет пользователей, которым сейчас можно назначить роль специалиста по педагогам.
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                {eligibleMcUsers.map(item => {
                  const isSpecialist = item.job_title === 'pedagogy_specialist'
                  const isLead = item.job_title === 'pedagogy_lead'

                  const roleLabel = isLead ? 'Руководитель' : isSpecialist ? 'Специалист' : 'Без роли'
                  const roleVariant = isLead ? 'default' : isSpecialist ? 'success' : 'neutral'
                  const roleCardClass = isLead
                    ? 'border-primary/30 bg-primary/5'
                    : isSpecialist
                      ? 'border-success/20 bg-success/5'
                      : 'border-border bg-card'

                  return (
                    <div key={item.user_id} className={`rounded-xl border p-3 transition-colors ${roleCardClass}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="text-sm font-medium">{displayName(item)}</div>
                            <Badge text={roleLabel} variant={roleVariant} className="px-2 py-0.5 text-[10px]" />
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {item.tg_username ? `@${item.tg_username}` : item.email || 'без контакта'}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            Активных франчайзи: {formatNumber(item.partners_count)}
                          </div>
                        </div>

                        <div className="flex flex-wrap justify-end gap-2">
                          <ActionButton
                            disabled={busyUserId === item.user_id}
                            onClick={() => handleToggleSpecialist(item, !isSpecialist)}
                          >
                            {isSpecialist ? 'Снять специалиста' : 'Назначить специалистом'}
                          </ActionButton>
                          <ActionButton
                            disabled={busyUserId === item.user_id}
                            onClick={() => handleToggleLead(item, !isLead)}
                          >
                            {isLead ? 'Снять руководителя' : 'Назначить руководителем'}
                          </ActionButton>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </BottomSheet>
      )}
    </div>
  )
}

/** Неприметная точка входа в настройки; цветная точка появляется только при риске. */
function CompactControlButton({
  icon,
  label,
  onClick,
  hasAttention = false,
}: {
  icon: ReactNode
  label: string
  onClick: () => void
  hasAttention?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="relative inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <span className="relative">
        {icon}
        {hasAttention && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-error ring-2 ring-background" aria-label="Требует проверки" />}
      </span>
      <span className="hidden sm:inline">{label}</span>
    </button>
  )
}

function SortButton({
  label,
  active,
  direction,
  onClick,
}: {
  label: string
  active: boolean
  direction: 'asc' | 'desc'
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 transition hover:text-foreground ${active ? 'text-foreground' : ''}`}
    >
      <span>{label}</span>
      <ArrowDownUp className={`h-3.5 w-3.5 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
      {active && <span className="text-[10px] text-primary">{direction === 'asc' ? '↑' : '↓'}</span>}
    </button>
  )
}
